import {
  Alert,
  Button,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useEInvoiceSettings, usePendingEInvoices, useSaveEInvoiceSettings } from '@/api/hooks'
import type { EInvoiceSettings } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'

const BLANK: EInvoiceSettings = {
  enabled: true,
  appliesFrom: undefined,
  portalName: '',
  portalUrl: '',
  defaultHsnCode: '996334',
  holdPrintUntilRegistered: false,
}

/**
 * How this company files its e-invoices.
 *
 * <p>Which bills need an IRN is not a setting - it is the law, and it follows
 * the customer's GSTIN. What is settable is whether this company files at
 * all, whose portal it files through, and whether an unsigned invoice may
 * leave the building.</p>
 */
export function EInvoiceTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data, error } = useEInvoiceSettings()
  const save = useSaveEInvoiceSettings()
  const { data: pending } = usePendingEInvoices()
  const [form, setForm] = useState<EInvoiceSettings>(BLANK)
  const [dirty, setDirty] = useState(false)

  const writable = canWrite('config')

  useEffect(() => {
    if (data) {
      setForm(data)
      setDirty(false)
    }
  }, [data])

  const set = (patch: Partial<EInvoiceSettings>) => {
    setForm((current) => ({ ...current, ...patch }))
    setDirty(true)
  }

  return (
    <>
      <ErrorBanner error={error ?? save.error} />

      <Typography variant="subtitle1">E-invoicing</Typography>
      <Typography variant="caption" color="text.secondary">
        A tax invoice to a customer registered under GST has to carry an IRN and a signed QR from the
        government portal. Which invoices those are follows the customer&apos;s GSTIN and is not a
        choice; what is set here is whether this company files, and how.
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                disabled={!writable}
                onChange={(event) => set({ enabled: event.target.checked })}
              />
            }
            label="File tax invoices with the e-invoice portal"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Turn this off for a company below the turnover threshold. Nothing is then asked for on any
            invoice.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Portal name"
            value={form.portalName ?? ''}
            disabled={!writable || !form.enabled}
            onChange={(event) => set({ portalName: event.target.value })}
            placeholder="NIC Invoice Registration Portal"
            helperText="Whoever this company actually files through - the portal, or a GSP"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Portal address"
            value={form.portalUrl ?? ''}
            disabled={!writable || !form.enabled}
            onChange={(event) => set({ portalUrl: event.target.value })}
            placeholder="https://einvoice1.gst.gov.in"
            helperText="Opened straight from an invoice's e-invoice screen"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.holdPrintUntilRegistered}
                disabled={!writable || !form.enabled}
                onChange={(event) => set({ holdPrintUntilRegistered: event.target.checked })}
              />
            }
            label="Hold the printed invoice until the portal signs it"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            On, an unsigned tax invoice cannot be printed at all — the customer is never handed a
            document that is not yet valid. Off, it prints with a notice that the IRN is to come.
          </Typography>
        </Grid>
      </Grid>

      {form.enabled && (pending?.length ?? 0) > 0 ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {pending?.length === 1
            ? '1 tax invoice is waiting on the portal'
            : `${pending?.length} tax invoices are waiting on the portal`}
          : {pending?.slice(0, 6).map((entry) => entry.invoiceNumber).join(', ')}
          {(pending?.length ?? 0) > 6 ? ' and more' : ''}.
          {form.portalUrl ? (
            <>
              {' '}
              <Link href={form.portalUrl} target="_blank" rel="noopener noreferrer">
                Open {form.portalName || 'the portal'}
              </Link>
            </>
          ) : null}
        </Alert>
      ) : null}

      {writable ? (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button
            disabled={!dirty}
            onClick={() => {
              setForm(data ?? BLANK)
              setDirty(false)
            }}
          >
            Discard changes
          </Button>
          <Button
            variant="contained"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate(form, { onSuccess: () => setDirty(false) })}
          >
            Save settings
          </Button>
        </Stack>
      ) : null}
    </>
  )
}
